import { inject, Injectable } from '@angular/core';
import { PDFExportComponent } from '@progress/kendo-angular-pdf-export';
import { exportPDF } from '@progress/kendo-drawing';
import { FileDownloadService } from './file-download.service';

/**
 * Saves the content of a `<kendo-pdf-export>` as a PDF file.
 *
 * `PDFExportComponent.saveAs()` can't be awaited: it returns `void` and keeps laying out the host
 * element in the background (`drawDOM` inserts a temporary container next to it). Print hosts
 * render `<kendo-pdf-export>` under an `*ngIf` flag and clear it right after `saveAs()`, which
 * removes the element mid-export and fails with "element.parentNode is null" whenever the images
 * weren't already decoded. `export()` is awaitable, so `save()` resolves only once the DOM is no
 * longer needed - hosts clear their flag in `finally`.
 */
@Injectable({
    providedIn: 'root'
})
export class PdfExportService {
    private fileDownloadService = inject(FileDownloadService);

    async save(pdf: PDFExportComponent, fileName: string): Promise<void> {
        const group = await pdf.export();
        const dataUri = await exportPDF(group, this.getPdfOptions(pdf));

        this.fileDownloadService.handleAttachmentDownload({
            base64Content: dataUri.substring(dataUri.indexOf(',') + 1),
            name: fileName
        });
    }

    // Mirrors `PDFExportComponent.pdfOptions()` (protected) from the component's public inputs, so
    // every host keeps the paper size, orientation and margins set on its own `<kendo-pdf-export>`.
    private getPdfOptions(pdf: PDFExportComponent) {
        return {
            autoPrint: pdf.autoPrint,
            author: pdf.author,
            creator: pdf.creator,
            date: pdf.date,
            imgDPI: pdf.imageResolution,
            keywords: pdf.keywords,
            landscape: pdf.landscape,
            margin: this.getMargin(pdf),
            multiPage: true,
            paperSize: pdf.paperSize,
            producer: pdf.producer,
            subject: pdf.subject,
            title: pdf.title
        };
    }

    private getMargin(pdf: PDFExportComponent) {
        if (!pdf.marginComponent) {
            return pdf.margin;
        }

        const margin = typeof pdf.margin === 'object' ? pdf.margin : {};
        return Object.assign({}, margin, pdf.marginComponent.options);
    }
}
